import { type ButtonHTMLAttributes, forwardRef } from "react";
import { Spinner } from "./spinner";

export type ButtonVariant = "primary" | "quiet" | "danger" | "outline";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	/** Shows a spinner, sets aria-busy and blocks clicks; the label stays so the width does not jump. */
	loading?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
	primary:
		"border border-board bg-board text-white shadow-[0_1px_2px_rgb(16_24_40/0.14),inset_0_1px_0_rgb(255_255_255/0.14)] hover:bg-board-deep hover:shadow-[0_4px_10px_-2px_color-mix(in_oklab,var(--color-board)_45%,transparent)] active:translate-y-px active:bg-board-deep",
	quiet: "border border-transparent bg-transparent text-ink hover:bg-ink/[0.06] active:bg-ink/[0.1]",
	outline: "border border-rule bg-sheet text-ink shadow-[var(--shadow-card)] hover:border-ink/25 hover:bg-paper active:translate-y-px active:bg-paper",
	danger: "border border-conflict bg-conflict text-sheet hover:brightness-90 active:brightness-85",
};

// Every size keeps the 2.75rem minimum touch target; `sm` only tightens padding and type.
const SIZE: Record<ButtonSize, string> = {
	sm: "min-h-touch px-[0.75rem] text-[0.875rem] gap-[0.375rem]",
	md: "min-h-touch px-[1rem] text-[0.9375rem] gap-[0.5rem]",
	lg: "min-h-[3.25rem] px-[1.375rem] text-[1.0625rem] gap-[0.625rem]",
};

/**
 * The one button — GMS's shape (0.5rem radius, 1px edge on every variant so
 * they line up), OVR's board blue. Sentence-case labels that say exactly what
 * happens ("Make official", not "Submit").
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{ variant = "primary", size = "md", loading = false, disabled, className = "", children, type = "button", ...rest },
	ref,
) {
	return (
		<button
			ref={ref}
			type={type}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			className={`inline-flex min-w-touch cursor-pointer select-none items-center justify-center rounded-btn font-bold leading-none transition-[background-color,border-color,box-shadow,transform,filter] duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT[variant]} ${SIZE[size]} ${className}`}
			{...rest}
		>
			{loading && <Spinner className="size-[1em]" />}
			{children}
		</button>
	);
});
